import {
  SORT_ORDER_TO_ALGOLIA_INDEX,
  ALGOLIA_INDEX_TO_SORT_ORDER,
} from 'commons/constants';
import {
  ALL_URL_PARAMS,
  PAGE_TYPE,
  URL_PARAM,
  getCategoryPageTypeConfig,
} from './categoryPageTypes';

const PRESERVED_URL_PARAMS = new Set(['showMultipleStores']);

const parseArrayParam = value => {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) {
    return value.flatMap(item =>
      String(item)
        .split(',')
        .filter(Boolean)
    );
  }
  return String(value)
    .split(',')
    .filter(Boolean);
};

// Every param this page family may write, not just the ones this page type
// owns: an unowned param has no widget that could clear it, so leaving it out
// here would make it survive every rebuild.
const getFilterParamKeys = (filters = []) =>
  new Set([...ALL_URL_PARAMS, ...(filters || []).map(({ id }) => id)]);

export const parseCategoryPageUrlState = (
  searchParams,
  filters = [],
  pageType = PAGE_TYPE.CATEGORY
) => {
  const {
    urlParams,
    skipFilterIds,
    defaultSortIndex,
    defaultSortOrder,
  } = getCategoryPageTypeConfig(pageType);
  const ownsParam = param => urlParams.includes(param);

  const params = Object.fromEntries(searchParams);
  const filterIds = new Set((filters || []).map(({ id }) => id));

  const sortBy = params.sortBy ?? defaultSortOrder;
  const currentProductIndex =
    SORT_ORDER_TO_ALGOLIA_INDEX[sortBy] ?? defaultSortIndex;

  const quickshipValues = ownsParam(URL_PARAM.QUICKSHIP)
    ? parseArrayParam(params.quickship)
    : [];
  const isReadyToShipActive =
    quickshipValues.includes('true') || quickshipValues.includes(true);

  const widthRangeValue = ownsParam(URL_PARAM.WIDTH_RANGE)
    ? parseArrayParam(params.widthRange)[0] ?? ''
    : '';
  const [minWidth = '', maxWidth = ''] = widthRangeValue.split('-');

  const searchQuery = ownsParam(URL_PARAM.SEARCH)
    ? parseArrayParam(params.search)[0] ?? ''
    : '';

  const selectedColors = ownsParam(URL_PARAM.COLOR)
    ? parseArrayParam(params.color).reduce(
        (acc, colorId) => ({
          ...acc,
          [colorId]: true,
        }),
        {}
      )
    : {};

  const selectedFacets = {};
  Object.entries(params).forEach(([key, value]) => {
    if (
      !filterIds.has(key) ||
      skipFilterIds.has(key) ||
      PRESERVED_URL_PARAMS.has(key)
    ) {
      return;
    }

    const filter = filters.find(({ id }) => id === key);
    if (!filter) return;

    const labels = new Set();
    parseArrayParam(value).forEach(optionId => {
      const option = filter.options?.find(({ id }) => id === optionId);
      if (option?.label) {
        labels.add(option.label);
      }
    });

    if (labels.size > 0) {
      selectedFacets[key] = labels;
    }
  });

  return {
    sortBy,
    currentProductIndex,
    isReadyToShipActive,
    minWidth,
    maxWidth,
    searchQuery,
    selectedColors,
    selectedFacets,
    colorIds: Object.keys(selectedColors),
  };
};

export const buildCategoryPageUrlParams = ({
  sortBy,
  isReadyToShipActive,
  minWidth,
  maxWidth,
  searchQuery,
  selectedColors,
  selectedFacets,
  filters = [],
  pages,
  existingParams = {},
  pageType = PAGE_TYPE.CATEGORY,
}) => {
  const { urlParams, defaultSortOrder } = getCategoryPageTypeConfig(pageType);
  const ownsParam = param => urlParams.includes(param);

  const filterParamKeys = getFilterParamKeys(filters);
  const params = Object.fromEntries(
    Object.entries(existingParams).filter(
      ([key]) => !filterParamKeys.has(key) || PRESERVED_URL_PARAMS.has(key)
    )
  );

  if (sortBy && sortBy !== defaultSortOrder) {
    params.sortBy = sortBy;
  }

  if (pages > 0) {
    params.pages = pages;
  }

  if (isReadyToShipActive && ownsParam(URL_PARAM.QUICKSHIP)) {
    params.quickship = 'true';
  }

  if ((minWidth || maxWidth) && ownsParam(URL_PARAM.WIDTH_RANGE)) {
    params.widthRange = `${minWidth || ''}-${maxWidth || ''}`;
  }

  if (searchQuery && ownsParam(URL_PARAM.SEARCH)) {
    params.search = searchQuery;
  }

  const activeColorIds = Object.entries(selectedColors || {})
    .filter(([, isSelected]) => isSelected)
    .map(([colorId]) => colorId);

  if (activeColorIds.length > 0 && ownsParam(URL_PARAM.COLOR)) {
    params.color = activeColorIds.join(',');
  }

  Object.entries(selectedFacets || {}).forEach(([attribute, values]) => {
    const filter = filters.find(({ id }) => id === attribute);
    if (!filter || !values?.size) return;

    const optionIds = [...values]
      .map(label => filter.options?.find(option => option.label === label)?.id)
      .filter(Boolean);

    if (optionIds.length > 0) {
      params[attribute] = optionIds.join(',');
    }
  });

  return params;
};

export const getSortByFromProductIndex = (
  productIndex,
  pageType = PAGE_TYPE.CATEGORY
) =>
  ALGOLIA_INDEX_TO_SORT_ORDER[productIndex] ??
  getCategoryPageTypeConfig(pageType).defaultSortOrder;

export const serializeFilterState = ({
  sortBy,
  isReadyToShipActive,
  minWidth,
  maxWidth,
  searchQuery,
  selectedColors,
  selectedFacets,
}) =>
  JSON.stringify({
    sortBy,
    isReadyToShipActive,
    minWidth,
    maxWidth,
    searchQuery,
    selectedColors,
    selectedFacets: Object.fromEntries(
      Object.entries(selectedFacets || {}).map(([key, values]) => [
        key,
        [...values].sort(),
      ])
    ),
  });
